import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { Card, Flex, Grid, GridItem } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { TipoModulo } from "@/services/aux-entities.service";
import { useRoles } from "@/hooks/useRoles";
import {
  fetchModuleTypesHandler,
  fetchTruckHandler,
  handleUpdateTruckHandler,
} from "../schema/handler-functions";
import { AppColors } from "@/styles/colors";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import {
  onlyUppercaseAlphanumeric,
  argentinianPlate,
  onlyAlphanumericAndSpaces,
} from "@/utils/reg-expressions";
import { isEmpty } from "@/utils/query-string";
import { Save } from "lucide-react";
import { TruckDetail, truckUpdateSchema } from "../schema/trucks.schema";

export const TruckUpdatePage = () => {
  const { id } = useParams();
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();

  const handleCancel = () => {
    navigate(AppRoutes.TRUCKS);
  };

  const { control, getValues, setError, clearErrors, setValue } = useForm({
    resolver: zodResolver(truckUpdateSchema),
  });
  const { fetchPermissions } = useRoles();

  const [moduleTypes, setModuleTypes] = useState<TipoModulo[] | null>([]);
  const [loadingModuleTypes, setLoadingModuleTypes] = useState(false);
  const [loadingUpdateTruck, setLoadingUpdateTruck] = useState(false);

  const [loadingTruck, setLoadingTruck] = useState(false);

  const [truck, setTruck] = useState<TruckDetail | null>(null);

  const fetchModuleTypes = async () => {
    await fetchModuleTypesHandler({
      setLoadingModuleTypes,
      setModuleTypes,
      fetchPermissions,
      navigate,
    });
  };

  const fetchTruck = async (id: number) => {
    await fetchTruckHandler({
      setLoadingTruck,
      getValues,
      navigate,
      id,
      fetchPermissions,
      setTruck,
    });
  };

  useEffect(() => {
    if (isActive && id) {
      fetchModuleTypes();
      fetchTruck(Number(id));
    }
  }, [isActive, id]);

  const handleUpdate = async () => {
    handleUpdateTruckHandler({
      id: Number(id),
      fetchPermissions,
      getValues,
      navigate,
      setLoadingUpdateTruck,
      values: getValues()
    });
  };

  const watchedValues = useWatch({
    control,
  });

  useEffect(() => {
    if (!watchedValues.patente) return clearErrors("patente");
    if (argentinianPlate.test(watchedValues.patente)) {
      clearErrors("patente");
    } else {
      setError("patente", {
        type: "pattern",
        message: "La patente debe tener el formato estándar: AAA000 o AA000AA",
      });
    }
  }, [watchedValues.patente]);

  const hasNoFilledValues = useMemo(() => {
    const { nombre, patente, modulos } = watchedValues || {};

    return !nombre || isEmpty(patente) || isEmpty(modulos);
  }, [watchedValues]);

  useEffect(() => {
    if (truck) {
      setValue("nombre", truck?.nombre);
      setValue("patente", truck?.patente);
      setValue(
        "modulos",
        truck?.modulos.map((item) => item.id),
      );
      setValue("activo", [truck?.activo]);
    }
  }, [truck]);

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="trucks-creation-page">
      <SectionHeader
        title="Maestro de camiones"
        addButtonLabel="Guardar cambios"
        addButtonIcon={<Save />}
        cancelButtonLabel="Cancelar"
        handleAdd={handleUpdate}
        handleCancel={handleCancel}
        loading={loadingUpdateTruck || loadingTruck}
        disableAdd={hasNoFilledValues || loadingUpdateTruck || loadingTruck || !argentinianPlate.test(watchedValues.patente)}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)" gap="16px">
            <GridItem>
              <InputField
                control={control}
                inputName="nombre"
                labelText="Nombre"
                placeholder="Ingresar nombre"
                required
                allowedPattern={onlyAlphanumericAndSpaces}
                maxLength={20}
              />
            </GridItem>
            <GridItem>
              <InputField
                control={control}
                inputName="patente"
                labelText="Patente"
                placeholder="Ingresar patente"
                required
                allowedPattern={onlyUppercaseAlphanumeric}
                maxLength={7}
              />
            </GridItem>
            <GridItem>
              <SelectComponent
                control={control}
                name="modulos"
                items={
                  moduleTypes?.map((item) => ({
                    label: item.descripcion,
                    value: item.id,
                  })) ?? []
                }
                loading={loadingModuleTypes}
                disabled={loadingModuleTypes}
                placeholder="Seleccionar tipo de modulo"
                clearable
                required
                multiple
                labelText="Tipo de pedido"
                minHeight="40px"
              />
            </GridItem>
            <GridItem>
              <SelectComponent
                control={control}
                name="activo"
                items={[
                  { label: "Activo", value: true },
                  { label: "Inactivo", value: false },
                ]}
                required
                labelText="Estado"
                minHeight="40px"
                disabled={loadingTruck || loadingUpdateTruck}
              />
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>
    </Flex>
  );
};
